import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { StatStrip } from "../../../../components/UIToolkit/molecules/StatStrip";
import { useLastNameFirst } from "../../../../hooks/useNameFormat";
import {
	ALPHA,
	CHANGE_HERO,
	FONT_SIZE,
	ICON_SIZE,
	INK,
	LETTER_SPACING,
	MOTION,
	NEUTRAL,
	RADIUS,
	SIDE_BY_SIDE_COMPARE,
	SURFACE,
} from "../../../../theme/tokens";
import { formatPersonFullName } from "../../../../utils/formatters";
import type { MergeNurseSummary } from "../../../../utils/nurseMergeModels";
import type { MovedGroupCount } from "./mergeLogic";

type Props = {
	kept: MergeNurseSummary;
	deleted: MergeNurseSummary;
	groups: MovedGroupCount[];
	otherCount: number;
};

/**
 * The confirm step's one focal moment: the deleted record folding into the kept one, and what travels
 * with it. On arrival the deleted record settles back (struck, dimmed) while the kept one lands — the
 * direction of the merge read at a glance, before any sentence below it.
 */
export const MergeConfirmHero: FC<Props> = ({ kept, deleted, groups, otherCount }) => {
	const { t } = useTranslation();
	const lastNameFirst = useLastNameFirst();
	const items = [
		...groups.map((group) => ({ label: t(`nurse.merge.moved.${group.id}`, { count: group.count }), value: group.count.toLocaleString() })),
		...(otherCount > 0 ? [{ label: t("nurse.merge.moved.other", { count: otherCount }), value: otherCount.toLocaleString() }] : []),
	].map((item, index) => ({ ...item, emphasis: index === 0, dataTestId: `merge-employees-moved-${index}` }));

	return (
		<Box
			data-testid="merge-employees-confirm-hero"
			sx={(theme) => ({
				display: "flex",
				flexDirection: "column",
				gap: 2,
				p: { xs: 2, sm: 2.5 },
				borderRadius: RADIUS.POPOVER / RADIUS.CARD,
				border: `1px solid ${alpha(theme.palette.primary.main, CHANGE_HERO.BORDER_ALPHA)}`,
				background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, CHANGE_HERO.WASH_ALPHA)} 0%, ${alpha(theme.palette.primary.main, 0)} ${CHANGE_HERO.WASH_STOP})`,
			})}
		>
			<Typography
				component="span"
				sx={{ fontSize: FONT_SIZE.LABEL_XS, fontWeight: 700, letterSpacing: LETTER_SPACING.LABEL, textTransform: "uppercase", color: "primary.dark" }}
			>
				{t("nurse.merge.confirm.eyebrow")}
			</Typography>
			<Box
				role="img"
				aria-label={t("nurse.merge.confirm.ribbon.aria", {
					deleted: formatPersonFullName(deleted, lastNameFirst),
					deletedId: deleted.nurseId,
					kept: formatPersonFullName(kept, lastNameFirst),
					keptId: kept.nurseId,
				})}
				sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}
			>
				<RecordPill summary={deleted} caption={t("nurse.merge.compare.badge.deleted")} tone="deleted" />
				<ArrowForwardRounded
					aria-hidden
					sx={{
						fontSize: ICON_SIZE.CONTROL,
						color: "primary.main",
						"[dir='rtl'] &": { transform: "scaleX(-1)" },
						...enter("mergeArrowEnter", MOTION.STAGGER.STEP),
					}}
				/>
				<RecordPill summary={kept} caption={t("nurse.merge.compare.badge.kept")} tone="kept" />
			</Box>
			{items.length > 0 ? (
				<StatStrip items={items} variant="band" surface="paper" dataTestId="merge-employees-moved" />
			) : (
				<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY }} data-testid="merge-employees-moved-none">
					{t("nurse.merge.confirm.nothing.moves", { name: formatPersonFullName(deleted, lastNameFirst) })}
				</Typography>
			)}
		</Box>
	);
};

const enter = (name: string, delayMs: number): SxProps<Theme> => ({
	[`@keyframes ${name}`]: { from: { opacity: 0, transform: `translateX(-${CHANGE_HERO.ARRIVE_OFFSET_PX}px)` }, to: { opacity: 1, transform: "translateX(0)" } },
	animation: `${name} ${MOTION.DURATION.ENTRANCE_MD}ms ${MOTION.EASE_OUT} ${delayMs}ms both`,
	"@media (prefers-reduced-motion: reduce)": { animation: "none" },
});

const RecordPill: FC<{ summary: MergeNurseSummary; caption: string; tone: "kept" | "deleted" }> = ({ summary, caption, tone }) => {
	const lastNameFirst = useLastNameFirst();
	const isKept = tone === "kept";
	return (
		<Box
			data-testid={`merge-employees-confirm-${tone}`}
			sx={(theme) => ({
				display: "flex",
				alignItems: "center",
				gap: 1,
				minWidth: 0,
				px: 1.5,
				py: 1,
				borderRadius: RADIUS.CONTROL / RADIUS.CARD,
				backgroundColor: SURFACE,
				border: isKept
					? `${SIDE_BY_SIDE_COMPARE.SELECTED_RING_WIDTH}px solid ${theme.palette.primary.main}`
					: `1px dashed ${alpha(theme.palette.error.main, ALPHA.STATUS_BORDER)}`,
				boxShadow: isKept ? `0 0 0 ${CHANGE_HERO.AFTER_HALO_PX}px ${alpha(theme.palette.primary.main, ALPHA.BRAND_TINT)}` : "none",
				...(isKept
					? {
							"@keyframes mergeKeptLand": {
								from: { opacity: 0, transform: `scale(${CHANGE_HERO.LAND_FROM_SCALE})` },
								to: { opacity: 1, transform: "scale(1)" },
							},
							animation: `mergeKeptLand ${MOTION.DURATION.POP}ms ${MOTION.EASE_OUT} ${MOTION.STAGGER.MAX}ms both`,
						}
					: {
							"@keyframes mergeDeletedSettle": { from: { opacity: 1 }, to: { opacity: ALPHA.MUTED_SECONDARY } },
							animation: `mergeDeletedSettle ${MOTION.DURATION.ENTRANCE_MD}ms ${MOTION.EASE_OUT} both`,
						}),
				"@media (prefers-reduced-motion: reduce)": { animation: "none", opacity: isKept ? 1 : ALPHA.MUTED_SECONDARY },
			})}
		>
			{isKept && <CheckCircleRounded aria-hidden sx={{ fontSize: ICON_SIZE.INLINE, color: "primary.main" }} />}
			<Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
				<Typography
					component="span"
					sx={{
						fontSize: FONT_SIZE.EMPHASIS,
						fontWeight: 700,
						lineHeight: 1.3,
						color: isKept ? INK.PRIMARY : INK.MUTED,
						textDecoration: isKept ? "none" : "line-through",
						textDecorationColor: NEUTRAL[450],
						overflowWrap: "anywhere",
					}}
				>
					{formatPersonFullName(summary, lastNameFirst)}
				</Typography>
				<Typography component="span" sx={{ fontSize: FONT_SIZE.CAPTION, color: isKept ? "primary.dark" : "error.main", fontWeight: 600 }}>
					{`#${summary.nurseId} · ${caption}`}
				</Typography>
			</Box>
		</Box>
	);
};
